// ./services/profileService.js
import axios from "axios";
import { getToken } from "auth/authenticate";
import moment from "moment"; // Need moment for DOB formatting

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// 🌟 CRITICAL FIX: Removed userId argument, using token-based endpoint 🌟
const fetchAuthenticatedPatientProfile = async () => {
  // The user ID is read by the server from the JWT token in the header.

  try {
    const token = getToken();
    if (!token) {
      throw new Error("Authentication token missing.");
    }

    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    // 🌟 Use the known working endpoint path 🌟
    const response = await axios.get(
      `${BASE_URL}user/patient/profile/`,
      config
    );

    // Adjust the return data structure to match what Step 2 needs
    const userData = response.data.data;

    // console.log("User's profile data from profileService code : ", userData);

    // Map backend data to the patientForm structure
    const mappedData = {
      // FIX: Prioritize explicit firstName/lastName from backend. Fallback to splitting if only 'name' exists.
      firstName:
        userData.firstName ||
        (userData.name ? userData.name.split(" ")[0] : "") ||
        "",
      lastName:
        userData.lastName ||
        (userData.name ? userData.name.split(" ").slice(1).join(" ") : "") ||
        "",
      birthDate: userData.dob || "",
      gender: userData.gender || "",
      address: userData.address || "",
      email: userData.User.email || "",
      phone: userData.User.phoneNumber || "", // Use 'phoneNumber' from your example response
      timezone: userData.User.timezone || "",
      bookingFor: "myself",
    };

    return mappedData;
  } catch (error) {
    console.error("Error fetching patient profile:", error);
    throw new Error(
      error.response?.data?.message || "Failed to fetch profile data."
    );
  }
};

/**
 * Fetches the logged-in user's own patient profile.
 * Endpoint: GET /user/patient/{userId} (Assuming token authentication is sufficient,
 * but the legacy code explicitly used userId in the path).
 * We will simplify it to use the token as we did for fetchAuthenticatedPatientProfile.
 */
const fetchSelfPatientProfile = async () => {
  try {
    const token = getToken();
    if (!token) throw new Error("Authentication token missing.");

    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    // Use the token-based profile fetch (previously fetchAuthenticatedPatientProfile)
    const response = await axios.get(
      `${BASE_URL}user/patient/profile/`,
      config
    );

    const userData = response.data.data;
    // Map to a useful structure, similar to your previous mapping
    return {
      patientId: userData.patientId, // Critical ID for booking
      firstName: userData.firstName || userData.username?.split(" ")[0] || "",
      lastName:
        userData.lastName ||
        userData.username?.split(" ").slice(1).join(" ") ||
        "",
      birthDate: userData.dob || "",
      gender: userData.gender || "",
      address: userData.address || "",
      email: userData.email, // If needed for payment/confirmation
      phoneNumber: userData.phoneNumber,
    };
  } catch (error) {
    console.error("Error fetching self patient profile:", error);
    throw error;
  }
};

/**
 * Fetches the list of dependents associated with the logged-in guardian/user.
 * Endpoint: GET /guardian-user/dependents/{userId}
 * The legacy code used userId in the path. We'll use the token + userId for robustness.
 */
const fetchDependentsList = async (userId) => {
  try {
    const token = getToken();
    if (!token) throw new Error("Authentication token missing.");

    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };

    // Use the explicit userId in the path as per the legacy code structure
    const response = await axios.get(
      `${BASE_URL}guardian-user/dependents/${userId}`,
      config
    );

    // Map the array of dependents
    return response.data.data.map((dep) => ({
      patientId: dep.patientId, // Critical ID for booking
      name: dep.name,
      dob: dep.dob,
      gender: dep.gender,
      address: dep.address || "",
      // Add other required fields from the dependent object
    }));
  } catch (error) {
    console.error("Error fetching dependents list:", error);
    throw error;
  }
};

/**
 * Creates a new dependent patient under the guardian.
 * Endpoint: POST /guardian-user/dependent/sign-up/patient
 * @param {object} dependentData - { name, dob, gender, address, guardianId }
 * @returns {string} The patientId of the newly created dependent.
 */
const createNewDependent = async (dependentFormData, guardianUserId) => {
  try {
    const token = getToken();
    if (!token) throw new Error("Authentication token missing.");

    const formattedData = {
      name: `${dependentFormData.firstName} ${dependentFormData.lastName}`,
      dob: moment(dependentFormData.birthDate).format("YYYY-MM-DD"),
      gender: dependentFormData.gender,
      address: dependentFormData.address,
      guardianId: guardianUserId,
    };

    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };

    const response = await axios.post(
      `${BASE_URL}guardian-user/dependent/sign-up/patient`,
      formattedData,
      config
    );

    const patientId = response.data?.data?.dependent?.patientId;
    if (!patientId) throw new Error("API response missing patient ID.");

    return patientId;
  } catch (error) {
    console.error("Error creating new dependent:", error);
    throw new Error(
      error.response?.data?.message || "Failed to register new dependent."
    );
  }
};

/**
 * Creates an appointment for a given patient.
 * Endpoint: POST /appointment/patient
 */
const createAppointmentForPatient = async (
  patientId,
  appointmentDetails,
  guardianUserId
) => {
  try {
    const token = getToken();
    if (!token) throw new Error("Authentication token missing.");

    // Note: The legacy code used FormData and multipart/form-data headers.
    // We simplify this to JSON unless the backend strictly requires FormData.
    const appointmentData = {
      doctorId: appointmentDetails.doctorId,
      patientId: patientId,
      date: appointmentDetails.appointmentDateUTC,
      slotTime: appointmentDetails.appointmentTimeUTC, // e.g., "14:30:00Z"
      appointmentType: "ONLINE", // Assuming constant
      purpose: "CONSULTING", // Assuming constant
      medicalDetails: "",
      message: "",
      amount: appointmentDetails.displayingFee,
      paymentId: "test_id", // Placeholder
      parentId: guardianUserId, // The guardian's user ID
    };

    const config = {
      headers: {
        "Content-Type": "application/json", // Assuming JSON body is accepted
        Authorization: `Bearer ${token}`,
      },
    };

    const response = await axios.post(
      `${BASE_URL}appointment/patient`,
      appointmentData,
      config
    );

    return response.data?.data; // Returns { appointmentId, other details... }
  } catch (error) {
    console.error("Error creating appointment:", error);
    throw new Error(
      error.response?.data?.message || "Failed to create appointment."
    );
  }
};

// ... include the createPayment and Payhere initialization logic here ...
// (Since the full payment logic is complex and relies on specific constants/imports,
// we assume you will move it into a dedicated paymentService or keep it
// consolidated in Step3Summary, which handles the final submit.)

export {
  fetchAuthenticatedPatientProfile, fetchSelfPatientProfile, fetchDependentsList, createNewDependent, createAppointmentForPatient
}